// Header — sidenav toggle, language switch (en/fr round-trip), user menu (profile + logout).
// Mirrors the Angular HeaderComponent.
import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Button,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import { Menu as MenuIcon, AccountCircle, ExitToApp, KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { changeLanguage } from '@/app/i18n';
import { appConfig } from '@/api/appConfig';

interface HeaderProps {
  onToggleSidebar: () => void;
  onOpenProfile: () => void;
}

export function Header({ onToggleSidebar, onOpenProfile }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth0();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const currentLang = i18n.language || appConfig.defaultLanguage;

  const onChangeLanguage = () => {
    const next = currentLang.toLowerCase() === 'fr' ? 'en' : 'fr';
    changeLanguage(next);
  };

  const onLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <IconButton onClick={onToggleSidebar} edge="start" aria-label="menu">
          <MenuIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button onClick={onChangeLanguage}>
            {currentLang.toLowerCase() === 'en'
              ? t('global.languages.fr', { defaultValue: 'FR' }).toUpperCase()
              : t('global.languages.en', { defaultValue: 'EN' }).toUpperCase()}
          </Button>
          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDown />}
            sx={{ textTransform: 'none' }}
          >
            <Avatar
              src={user?.picture}
              alt={user?.name ?? 'user'}
              sx={{ width: 28, height: 28, mr: 1 }}
            />
            {user?.nickname ?? user?.name ?? user?.email ?? ''}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onOpenProfile();
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              {t('pages.profile.profile_label', { defaultValue: 'Profile' })}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onLogout();
              }}
            >
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
